import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LICENCE_BODIES, LICENCE_BODY_LABELS, type LicenceBody } from '@wodi/domain';
import type { PractitionerListView, PractitionerView } from '@wodi/shared';
import {
  Button,
  DateField,
  Dialog,
  DialogActions,
  Notice,
  RadioGroup,
  Select,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { practitionerKeys, savePractitioner } from '../api/practitioners.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './practitioners.module.css';

// Add or change one practitioner's profile (docs/plans/phase1-consultation.md §7.4).

const councilOptions = LICENCE_BODIES.map((b) => ({ id: b, label: LICENCE_BODY_LABELS[b] }));

export function PractitionerDialog({
  list,
  editing,
  onClose,
}: {
  list: PractitionerListView;
  /** The profile to change; undefined to add one for someone without a profile. */
  editing?: PractitionerView;
  onClose: () => void;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<string | null>(editing?.userId ?? null);
  const [cadre, setCadre] = useState<string | null>(editing?.cadre?.code ?? null);
  const [council, setCouncil] = useState<LicenceBody | null>(editing?.licenceBody ?? null);
  const [number, setNumber] = useState(editing?.licenceNumber ?? '');
  const [expiresOn, setExpiresOn] = useState<string | null>(editing?.licenceExpiresOn ?? null);
  const [issuedOn, setIssuedOn] = useState<string | null>(editing?.licenceIssuedOn ?? null);
  const [status, setStatus] = useState<'active' | 'inactive'>(editing?.status ?? 'active');
  const [tried, setTried] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      savePractitioner(userId ?? '', {
        cadreCode: cadre ?? '',
        licenceBody: council ?? 'other',
        licenceNumber: number.trim(),
        licenceExpiresOn: expiresOn,
        licenceIssuedOn: issuedOn,
        status,
      }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: practitionerKeys.list });
      await queryClient.invalidateQueries({ queryKey: practitionerKeys.me });
      toasts.show({ title: 'Practitioner saved', description: saved.displayName });
      onClose();
    },
  });

  const missing = {
    user: userId === null,
    cadre: cadre === null,
    council: council === null,
    number: number.trim() === '',
  };
  // ISO dates compare correctly as text.
  const issuedAfterExpiry = issuedOn !== null && expiresOn !== null && issuedOn > expiresOn;
  const submit = () => {
    setTried(true);
    if (Object.values(missing).some(Boolean) || issuedAfterExpiry) return;
    save.mutate();
  };

  return (
    <Dialog
      title={editing === undefined ? 'Add practitioner' : `Change ${editing.displayName}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <form
        className={styles.form}
        aria-label="Practitioner details"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {editing === undefined ? (
          <Select
            label="Staff member"
            placeholder="Choose a person"
            options={list.candidates.map((u) => ({ id: u.userId, label: u.displayName }))}
            value={userId}
            onChange={(key) => {
              setUserId(key === null ? null : String(key));
            }}
            {...(tried && missing.user ? { errorMessage: 'Choose who this is for.' } : {})}
          />
        ) : null}
        <Select
          label="Cadre"
          description="A provisional list, until the clinical lead confirms it."
          placeholder="Choose a cadre"
          options={list.cadres.map((c) => ({ id: c.code, label: c.display }))}
          value={cadre}
          onChange={(key) => {
            setCadre(key === null ? null : String(key));
          }}
          {...(tried && missing.cadre ? { errorMessage: 'Choose a cadre.' } : {})}
        />
        <Select
          label="Licensing council"
          placeholder="Choose a council"
          options={councilOptions}
          value={council}
          onChange={(key) => {
            setCouncil(key === null ? null : (String(key) as LicenceBody));
          }}
          {...(tried && missing.council ? { errorMessage: 'Choose the council.' } : {})}
        />
        <TextField
          label="Licence number"
          description="As the council's practising licence shows it."
          value={number}
          onChange={setNumber}
          {...(tried && missing.number ? { errorMessage: 'Enter the licence number.' } : {})}
        />
        <DateField
          label="Licence issued on"
          description="Optional. The day the current licence was issued."
          value={issuedOn}
          onChange={setIssuedOn}
          {...(issuedAfterExpiry
            ? { errorMessage: 'The issue date is after the expiry date. Check both dates.' }
            : {})}
        />
        <DateField
          label="Licence expires on"
          description="The last day the licence is valid. Leave empty only if you don't know it yet: Finish consultation will warn until it is entered."
          value={expiresOn}
          onChange={setExpiresOn}
        />
        {editing === undefined ? null : (
          <RadioGroup
            label="Status"
            orientation="horizontal"
            value={status}
            onChange={(v) => {
              setStatus(v === 'inactive' ? 'inactive' : 'active');
            }}
            options={[
              { id: 'active', label: 'Active' },
              { id: 'inactive', label: 'Inactive (no longer practising here)' },
            ]}
          />
        )}
        {save.isError ? (
          <Notice tone="error">
            {errorText(save.error, "The details weren't saved. Try again.")}
          </Notice>
        ) : null}
        {online ? null : (
          <Notice tone="info">You're offline. Saving works again when you're back online.</Notice>
        )}
      </form>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={submit}
        >
          Save practitioner
        </Button>
      </DialogActions>
    </Dialog>
  );
}
